import {
  ENTITY_CLASS,
  Fall,
  FallKey,
  type FallQueryFilters,
  type QueryOptions,
  streamError,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { fetchData } from "./functions";
import { QueryKey } from "./query";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export async function findFall(fallKey: FallKey | false | null | undefined) {
  if (!fallKey) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Fall", input: { fallKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function createFallQueryKey(fallKey?: FallKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.FALL, fallKey);
}

export function updateFallCache(fall: Fall) {
  TANSTACK_QUERY_CLIENT.setQueryData(createFallQueryKey(fall.fallKey), fall);
}

export function removeFallCache(fallKey: FallKey) {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ exact: true, queryKey: createFallQueryKey(fallKey) });
}

export function useFall(
  fallKey: FallKey | false | null | undefined,
  options?: {
    /** 仮想化された一覧で表示される場合に`true`を設定するフラグ */
    virtualized?: boolean;
  },
) {
  const { data: fall } = useQuery({
    queryKey: createFallQueryKey(fallKey),
    async queryFn() {
      return findFall(fallKey);
    },

    enabled: !!fallKey,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: !options?.virtualized,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return fall;
}

// =============================================================
// 一覧
// =============================================================

function createFallsQueryKey(...[queries, options]: ReadonlyArray<any>) {
  return QueryKey(ENTITY_CLASS.FALL + "List", queries, options);
}

export function removeFallsCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: createFallsQueryKey().slice(0, 1) });
}

export async function listFalls(filters?: FallQueryFilters, options?: QueryOptions) {
  const { output } = await fetchData({ type: "Entities::Falls", input: { filters, options } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

export function useListedFalls(
  filters?: FallQueryFilters,
  options?: QueryOptions & {
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled?: boolean;
  },
) {
  const { data, error, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: createFallsQueryKey(filters, options),
    async queryFn(context) {
      return listFalls(filters, { ...options, nextPageToken: context.pageParam.nextPageToken });
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  useEffect(
    function () {
      if (error) {
        streamError(error);
      }
    },
    [error],
  );

  return useMemo(
    function () {
      return {
        error,
        fetchNextPage,
        loaded: !hasNextPage,
        loading: isLoading,
        pages: data?.pages ?? [],
      };
    },
    [data?.pages, error, fetchNextPage, hasNextPage, isLoading],
  );
}
